import React, { FC } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import ControlButton from 'studio_react/common/components/VideoPlayer/ControlButton';
import { ReactComponent as ExpandIcon } from 'studio_react/svg/common/expand.svg';
import { ReactComponent as MinimizeIcon } from 'studio_react/svg/common/minimize.svg';
import { ReactComponent as PauseIcon } from 'studio_react/svg/common/pause.svg';
import { ReactComponent as PlayIcon } from 'studio_react/svg/common/play.svg';
import { ReactComponent as VolumeIcon1 } from 'studio_react/svg/common/volume-1.svg';
import { ReactComponent as VolumeIcon2 } from 'studio_react/svg/common/volume-2.svg';
import { ReactComponent as VolumeIconX } from 'studio_react/svg/common/volume-x.svg';
import { ReactComponent as VolumeIcon0 } from 'studio_react/svg/common/volume.svg';
import { useTranslate } from 'ts/common/hooks';

const formatTime = (s: number): string => {
    if (!isFinite(s) || isNaN(s)) {
        return '0:00';
    }

    const totalSeconds = Math.round(s);
    const m = Math.floor(totalSeconds / 60);
    const sec = totalSeconds % 60;

    return `${m}:${sec.toString().padStart(2, '0')}`;
};

/**
 * Diameter of the round slider thumb. The hover-reveal container around the volume slider must be
 * at least this tall: it clips with `overflow: hidden` to collapse horizontally, and a shorter box
 * crops the thumb's top and bottom, leaving a squared-off stub instead of a circle.
 */
const THUMB_SIZE = '12px';

/**
 * Height of the volume slider's reveal container. Taller than the thumb because that container
 * clips (`overflow: hidden`), and the browser's focus ring is drawn outside the 4px track — at
 * thumb height the ring is cropped away entirely and the slider looks unfocusable even though it
 * has focus.
 */
const VOLUME_TRACK_BOX_HEIGHT = '22px';

/** Width of the volume slider itself. */
const VOLUME_TRACK_WIDTH = '72px';

/**
 * Width of the volume slider's reveal container, and the width it expands to. Wider than the
 * track for the same reason the height is taller than the thumb: the focus ring is drawn outside
 * the track, so at track width the ring's end caps are clipped off and it reads as two loose
 * lines rather than a ring.
 */
const VOLUME_TRACK_BOX_WIDTH = '82px';

const sliderSx = {
    appearance: 'none',
    WebkitAppearance: 'none',
    height: '4px',
    borderRadius: '2px',
    // Suppressed while unfocused, then handed straight back to the browser on keyboard focus.
    // `revert` — not `auto` — because the bar's control buttons declare no outline at all, so
    // they render the UA's own ring; rolling this back to the UA stylesheet reproduces it exactly
    // (same style, colour and width), while the `auto` shorthand would also reset colour and
    // width to their initial values and draw a wider grey ring instead. Letting the browser pick
    // is the point: Chrome, Safari and forced-colors mode each use a different ring, and a
    // literal colour could only ever match one of them.
    //
    // These sliders are arrow-key operated, so leaving them with no indicator at all would strand
    // a keyboard user with no idea which control they are on (WCAG 2.4.7).
    outline: 'none',
    '&:focus-visible': {
        outline: 'revert'
    },
    cursor: 'pointer',
    '&::-webkit-slider-thumb': {
        WebkitAppearance: 'none',
        appearance: 'none',
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: '50%',
        background: 'white',
        cursor: 'pointer'
    },
    '&::-moz-range-thumb': {
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: '50%',
        background: 'white',
        cursor: 'pointer',
        border: 'none'
    }
};

interface ISeekBarProps {
    currentTime: number;
    totalDuration: number;
    seekLabel: string;
    onSeek: (time: number) => void;
}

/**
 * The scrub slider and its elapsed/total readout. Its own component so the surfaces that scrub
 * elsewhere can leave it out — see `showSeekBar`.
 */
const SeekBar: FC<ISeekBarProps> = ({ currentTime, totalDuration, seekLabel, onSeek }) => {
    const fillPercent =
        totalDuration > 0 ? Math.min(100, Math.max(0, (currentTime / totalDuration) * 100)) : 0;

    return (
        <>
            <Box
                as="input"
                type="range"
                min={0}
                max={totalDuration || 1}
                value={currentTime}
                step={0.1}
                aria-label={seekLabel}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onSeek(Number(e.target.value))
                }
                flex={1}
                minW={0}
                style={{
                    background: `linear-gradient(to right, white ${fillPercent}%, rgba(255,255,255,0.3) ${fillPercent}%)`
                }}
                sx={sliderSx}
            />

            <Text color="white" fontSize="14px" whiteSpace="nowrap" flexShrink={0}>
                {formatTime(currentTime)} / {formatTime(totalDuration)}
            </Text>
        </>
    );
};

interface ISlideshowControlsBaseProps {
    isPlaying: boolean;
    isFullscreen: boolean;
    /** Music playback volume (0–1). */
    volume: number;
    isMuted: boolean;
    /**
     * Whether this slideshow has any music to govern. False hides the sound control entirely,
     * rather than offering a mute button that silences nothing.
     */
    hasAudio: boolean;
    /** Makes the play/pause button reachable as a focus target from outside the bar. */
    playPauseRef?: React.Ref<HTMLButtonElement>;
    /**
     * Marks play/pause unavailable — there is nothing to play — while keeping it mounted and
     * focusable. The slideshow editor sets this for a slideshow with no slides yet, where the
     * button is its modal's initial focus target.
     */
    playPauseDisabled?: boolean;
    onPlayPause: () => void;
    onToggleMute: () => void;
    onVolumeChange: (value: number) => void;
    onToggleFullscreen: () => void;
}

type ISlideshowControlsSeekProps =
    | {
          /** Omit (or pass true) to render the seek slider and the elapsed/total readout. */
          showSeekBar?: true;
          currentTime: number;
          totalDuration: number;
          onSeek: (time: number) => void;
      }
    | {
          /**
           * Hides the seek slider and the readout, for a surface that already scrubs some other
           * way. The slideshow editor sets this: its timeline is the scrubbing surface, and a
           * second one on the same screen would compete with it. It also keeps the bar off the
           * playback clock entirely, so it stays out of the per-frame render path the editor
           * works hard to avoid (FOR-16442, FOR-16498).
           */
          showSeekBar: false;
      };

type ISlideshowControlsProps = ISlideshowControlsBaseProps & ISlideshowControlsSeekProps;

const SlideshowControls: FC<ISlideshowControlsProps> = (props) => {
    const {
        isPlaying,
        isFullscreen,
        volume,
        isMuted,
        hasAudio,
        playPauseRef,
        playPauseDisabled = false,
        onPlayPause,
        onToggleMute,
        onVolumeChange,
        onToggleFullscreen
    } = props;
    const t = useTranslate('studio.common.slideshowPlayer');
    const volumeFillPercent = isMuted ? 0 : volume * 100;

    const VolumeIcon =
        isMuted || volume === 0
            ? VolumeIconX
            : volume <= 0.33
            ? VolumeIcon0
            : volume <= 0.66
            ? VolumeIcon1
            : VolumeIcon2;

    const FullscreenIcon = isFullscreen ? MinimizeIcon : ExpandIcon;

    return (
        <Box position="absolute" bottom={0} left={0} right={0} zIndex={2}>
            <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                h="120px"
                background="linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)"
                pointerEvents="none"
                zIndex={0}
            />

            <Flex
                direction="column"
                position="relative"
                zIndex={1}
                px={{ base: '8px', md: '15px' }}
                pb={{ base: '10px', md: '17px' }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
                <Flex
                    h={{ base: '32px', md: '40px' }}
                    alignItems="center"
                    gap={{ base: '2px', md: '4px' }}
                    minW={0}
                >
                    <ControlButton
                        ref={playPauseRef}
                        onClick={onPlayPause}
                        ariaLabel={isPlaying ? t('pause') : t('play')}
                        ariaDisabled={playPauseDisabled}
                        bg="rgba(0,0,0,0.6)"
                        w={{ base: '36px', md: '48px' }}
                        h={{ base: '32px', md: '40px' }}
                    >
                        {isPlaying ? (
                            <PauseIcon width={20} height={20} />
                        ) : (
                            <PlayIcon width={20} height={20} />
                        )}
                    </ControlButton>

                    {/* Without the seek slider there is nothing here that wants the leftover
                        width, so the pill hugs its buttons instead of stretching empty. */}
                    <Flex
                        flex={props.showSeekBar === false ? '0 0 auto' : 1}
                        minW={0}
                        bg="rgba(0,0,0,0.6)"
                        px={{ base: '6px', md: '10px' }}
                        py="4px"
                        rounded="4px"
                        gap={{ base: '4px', md: '12px' }}
                        h={{ base: '32px', md: '40px' }}
                        alignItems="center"
                    >
                        {props.showSeekBar !== false && (
                            <SeekBar
                                currentTime={props.currentTime}
                                totalDuration={props.totalDuration}
                                seekLabel={t('seek')}
                                onSeek={props.onSeek}
                            />
                        )}

                        {/* Sound — mute toggle plus a slider revealed on hover, mirroring the
                            video player's control. Hidden outright for a slideshow with no
                            songs on its sound timeline: there is nothing to govern. */}
                        {hasAudio && (
                            <Flex alignItems="center" gap="6px" role="group">
                                <ControlButton
                                    onClick={onToggleMute}
                                    ariaLabel={isMuted ? t('unmute') : t('mute')}
                                >
                                    <VolumeIcon width={24} height={24} />
                                </ControlButton>

                                {/* Collapsed until revealed, but only on a device that can hover:
                                    touch never fires hover, and the mute button is this box's
                                    sibling rather than its child, so `:focus-within` can't stand
                                    in — a collapsed slider there could never be opened at all.
                                    Touch therefore gets it at full width.

                                    Where hover does exist, `:focus-within` matters as much as the
                                    group hover: the slider stays in the tab order while collapsed,
                                    so without it a keyboard user lands on a control clipped to
                                    zero width. */}
                                <Box
                                    w={VOLUME_TRACK_BOX_WIDTH}
                                    h={VOLUME_TRACK_BOX_HEIGHT}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    overflow="hidden"
                                    flexShrink={0}
                                    sx={{
                                        '@media (hover: hover)': {
                                            width: '0px',
                                            transition: 'width 0.2s ease',
                                            '[role=group]:hover &, &:focus-within': {
                                                width: VOLUME_TRACK_BOX_WIDTH
                                            }
                                        }
                                    }}
                                >
                                    <Box
                                        as="input"
                                        type="range"
                                        min={0}
                                        max={1}
                                        step={0.02}
                                        value={isMuted ? 0 : volume}
                                        aria-label={t('volume')}
                                        // Without this a screen reader reads the raw 0–1 value
                                        // ("0.5"); the level is meaningful to a listener as a
                                        // percentage.
                                        aria-valuetext={`${Math.round(
                                            (isMuted ? 0 : volume) * 100
                                        )}%`}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            onVolumeChange(Number(e.target.value))
                                        }
                                        w={VOLUME_TRACK_WIDTH}
                                        display="block"
                                        style={{
                                            background: `linear-gradient(to right, white ${volumeFillPercent}%, rgba(255,255,255,0.3) ${volumeFillPercent}%)`
                                        }}
                                        sx={sliderSx}
                                    />
                                </Box>
                            </Flex>
                        )}

                        <ControlButton
                            onClick={onToggleFullscreen}
                            ariaLabel={isFullscreen ? t('exitFullscreen') : t('fullscreen')}
                        >
                            <Box
                                w="24px"
                                h="24px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <FullscreenIcon width={20} height={20} />
                            </Box>
                        </ControlButton>
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    );
};

export default SlideshowControls;
